import { useState, useEffect, useContext } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Search, Plus, Grid, List, SortAsc } from 'lucide-react';
import { ChainlitContext, operationsState, operationModalState } from '@chainlit/react-client';
import { operationsService } from '@/services/operationsService';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import OperationCard from './OperationCard';

/**
 * Displays a searchable, sortable list of all operations.
 * Supports grid and list view modes with filtering by name or description.
 */
export default function OperationsList() {
  const apiClient = useContext(ChainlitContext);
  const [operations, setOperations] = useRecoilState(operationsState);
  const setModalState = useSetRecoilState(operationModalState);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'threads'>('date');

  const filteredAndSortedOperations = operations
    .filter(operation =>
      operation.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      operation.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'threads':
          return b.threadIds.length - a.threadIds.length;
        case 'date':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

  useEffect(() => {
    loadOperations();
  }, []);

  /**
   * Loads all operations from the API.
   */
  const loadOperations = async () => {
    setIsLoading(true);
    try {
      const operationsData = await operationsService.getAllOperations(apiClient);
      setOperations(operationsData);
    } catch (error) {
      console.error('Failed to load operations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Opens the create modal for a new operation.
   */
  const handleCreateNew = () => {
    setModalState({
      isOpen: true,
      mode: 'create'
    });
  };

  /**
   * Opens the edit modal for the specified operation.
   */
  const handleEdit = (operationId: string) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      operationId
    });
  };

  /**
   * Removes the deleted operation from the local state.
   */
  const handleDelete = (operationId: string) => {
    setOperations(prev => prev.filter(op => op.id !== operationId));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 px-2 py-1">
      {/* Enhanced Header Section */}
      <div className="space-y-6">
        {/* Title and Stats */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Operations</h1>
            <p className="text-muted-foreground">
              Manage your operations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="px-3 py-1">
              {operations.length} total
            </Badge>
            <Button onClick={handleCreateNew} size="lg" className="shadow-lg">
              <Plus className="mr-2 h-4 w-4" />
              New Operation
            </Button>
          </div>
        </div>

        {/* Enhanced Search and Controls */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search operations by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 bg-background/50 border-2 focus:border-primary/50 transition-colors"
            />
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-11">
                  <SortAsc className="mr-2 h-4 w-4" />
                  Sort by {sortBy}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('date')}>
                  Date Created
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('name')}>
                  Name
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('threads')}>
                  Thread Count
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Mode Toggle */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-9 px-3"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-9 px-3"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-6" />

      {/* Operations Display */}
      {filteredAndSortedOperations.length === 0 ? (
        <div className="text-center py-16">
          {searchQuery ? (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-muted/50 rounded-full flex items-center justify-center">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-muted-foreground">No operations found</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  No operations match your search for "{searchQuery}". Try adjusting your search terms or create a new operation.
                </p>
              </div>
              <Button onClick={handleCreateNew} variant="outline" size="lg">
                <Plus className="mr-2 h-4 w-4" />
                Create New Operation
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
                <Plus className="h-10 w-10 text-primary" />
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl font-semibold text-muted-foreground">No operations yet</h3>
                <p className="text-muted-foreground max-w-lg mx-auto leading-relaxed">
                  Operations help you organize related chat threads together. 
                  Create your first operation to start organizing your conversations.
                </p>
              </div>
              <Button onClick={handleCreateNew} size="lg" className="shadow-lg">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Operation
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Results Summary */}
          {searchQuery && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="px-3 py-1">
                  {filteredAndSortedOperations.length} result{filteredAndSortedOperations.length !== 1 ? 's' : ''}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  for "{searchQuery}"
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSearchQuery('')}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear search
              </Button>
            </div>
          )}

          {/* Operations Grid/List */}
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
          }>
            {filteredAndSortedOperations.map((operation) => (
              <OperationCard
                key={operation.id}
                operation={operation}
                onEdit={handleEdit}
                onDelete={handleDelete}
                viewMode={viewMode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

